/**
 * RowActions
 *
 * The portal's per-row actions used to be a row of small icon buttons whose only label was a
 * hover tooltip. A phone has no hover, so on a card that was three unlabelled 30px glyphs next
 * to the tap target that opens the card. This draws the same list of actions two ways:
 *
 *  - `icons`  (table rows)  — icon buttons with a tooltip AND an aria-label; an action without
 *                             an icon becomes a text button.
 *  - `labels` (phone cards) — the first `inline` actions as labelled text buttons, the rest in
 *                             a "More" menu. Destructive actions always go in the menu, so a
 *                             delete is never the thing a thumb lands on by accident.
 *
 * Actions describe what to do; confirmation is the page's job (open a ConfirmDialog from
 * `onClick`).
 *
 *   const actions = (u: User): RowAction[] => [
 *     { key: 'edit', label: 'Edit', icon: <Edit />, onClick: () => edit(u) },
 *     { key: 'delete', label: 'Delete', icon: <Delete />, color: 'error', onClick: () => askDelete(u) },
 *   ]
 *   <DataTable getRowActions={actions} ... />
 */

import { ReactNode, useState } from 'react'
import { Box, Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material'
import { MoreVert } from '@mui/icons-material'

export interface RowAction {
  key: string
  label: string
  icon?: ReactNode
  onClick: () => void
  /** `error` marks a destructive action: red, and always in the overflow menu on a card. */
  color?: 'primary' | 'error' | 'warning' | 'success'
  disabled?: boolean
  /** Shown as the tooltip (and menu secondary text) when it says more than `label`. */
  hint?: string
}

export interface RowActionsProps {
  actions: RowAction[]
  variant: 'icons' | 'labels'
  /** In `labels` mode, how many non-destructive actions stay visible before "More". Default 2. */
  inline?: number
}

export function RowActions({ actions, variant, inline = 2 }: RowActionsProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  if (actions.length === 0) return null

  if (variant === 'icons') {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
        {actions.map((a) =>
          a.icon ? (
            <Tooltip key={a.key} title={a.hint ?? a.label}>
              {/* span: a disabled button fires no events, and Tooltip needs them. */}
              <span>
                <IconButton
                  aria-label={a.label}
                  onClick={a.onClick}
                  disabled={a.disabled}
                  color={a.color ?? 'default'}
                  size="small"
                >
                  {a.icon}
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Button key={a.key} size="small" onClick={a.onClick} disabled={a.disabled} color={a.color ?? 'primary'}>
              {a.label}
            </Button>
          ),
        )}
      </Box>
    )
  }

  const safe = actions.filter((a) => a.color !== 'error')
  // Everything fits: no menu at all, even for a destructive action — a menu holding one item is
  // just an extra tap.
  const fitsInline = actions.length <= inline
  const visible = fitsInline ? actions : safe.slice(0, inline)
  const overflow = fitsInline ? [] : actions.filter((a) => !visible.includes(a))

  return (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
      {visible.map((a) => (
        <Button
          key={a.key}
          onClick={a.onClick}
          disabled={a.disabled}
          color={a.color ?? 'primary'}
          startIcon={a.icon}
          sx={{ minHeight: 40 }}
        >
          {a.label}
        </Button>
      ))}
      {overflow.length > 0 && (
        <>
          <IconButton aria-label="More actions" onClick={(e) => setAnchor(e.currentTarget)} sx={{ width: 40, height: 40 }}>
            <MoreVert />
          </IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
            {overflow.map((a) => (
              <MenuItem
                key={a.key}
                disabled={a.disabled}
                onClick={() => {
                  setAnchor(null)
                  a.onClick()
                }}
                sx={{ minHeight: 44, color: a.color ? `${a.color}.main` : undefined }}
              >
                {a.icon && <ListItemIcon sx={{ color: 'inherit' }}>{a.icon}</ListItemIcon>}
                <ListItemText primary={a.label} secondary={a.hint} />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  )
}
